<script setup lang="ts">
import { CategoryType, type Event } from "mws-ts-v2-sdk";

import { OG_IMAGE_SIZE } from "@/constants/metaTags";

import type { EventPageStoryblok } from "~/storyblok/.generated/component-types";

const { event, metaTags } = defineProps<{
  event: Event;
  metaTags?: EventPageStoryblok["metaTags"];
}>();

const platformStore = usePlatformStore();
const platformName = platformStore.getPlatformName;
const i18n = useI18n();

const sportCategory = computed(() =>
  event.categories.find(
    (category) => category.externalReference?.type === CategoryType.Sport,
  ),
);

const leagueCategory = computed(() =>
  event.categories.find(
    (category) => category.externalReference?.type === CategoryType.League,
  ),
);

const eventDate = computed(() => {
  const startDate = new Date(event.startDate);

  return startDate.toLocaleDateString();
});

const isFootballTeamPage = computed(
  () => sportCategory.value?.name === "Football",
);

const getMetaLabel = (name?: string) => (name ? `${name} -` : "");

const footballShirtsTitle = computed(
  () =>
    `${event.name} - ${getMetaLabel(leagueCategory.value?.name)} ${eventDate.value} - ${getMetaLabel(sportCategory.value?.name)} MatchWornShirt`,
);

const title = computed(() => {
  if (isFootballTeamPage.value) return footballShirtsTitle.value;

  if (metaTags?.title) return metaTags.title;

  return `${event.name} | ${platformName}`;
});

const ogTitle = computed(() => {
  if (isFootballTeamPage.value) return footballShirtsTitle.value;

  if (metaTags?.og_title) return metaTags.og_title;

  return title.value;
});

const description = computed(() => {
  if (isFootballTeamPage.value)
    return i18n.t("event.metaTags.footballShirts.description", {
      eventName: event.name,
      leagueName: getMetaLabel(leagueCategory.value?.name),
      eventDate: eventDate.value,
    });

  if (metaTags?.description) return metaTags.description;

  return i18n.t("event.metaTags.defaultDescription", {
    eventName: event.name,
  });
});

const ogDescription = computed(() => {
  if (isFootballTeamPage.value)
    return i18n.t("event.metaTags.footballShirts.description", {
      eventName: event.name,
      leagueName: getMetaLabel(leagueCategory.value?.name),
      eventDate: eventDate.value,
    });

  if (metaTags?.og_description) return metaTags.og_description;

  return description.value;
});

const ogImage = computed(() => {
  if (metaTags?.og_image) return metaTags.og_image;

  const platformLogo =
    platformStore.getPlatformConfiguration.assets.primaryLogo;

  return `${platformLogo}?w=${OG_IMAGE_SIZE}&h=${OG_IMAGE_SIZE}&fit=contain`;
});
</script>

<template>
  <LayoutMetaTags
    :meta-tags="{
      title,
      og_title: ogTitle,
      description,
      og_description: ogDescription,
      og_image: ogImage ?? undefined,
    }"
  />
</template>
